import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Layout from "../PageLayout/LoginLayout";
import { verifyEmailCode, sendVerificationCode } from "../services/auth.service";

export default function VerifyCode() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const emailFromQuery = params.get("email");
  const emailFromState = location.state?.email;
  let email = emailFromState || emailFromQuery || "";
  if (!email) {
    try {
      email = localStorage.getItem("pendingVerificationEmail") || "";
    } catch (_) {
      email = "";
    }
  }

  const [code, setCode] = useState(Array(6).fill(""));
  const [timeLeft, setTimeLeft] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleChange = (value, index) => {
    if (/^[0-9]?$/.test(value)) {
      const updated = [...code];
      updated[index] = value;
      setCode(updated);

      if (value && index < 5) {
        const next = document.getElementById(`code-${index + 1}`);
        if (next) next.focus();
      }
    }
  };

  const handleResend = async () => {
    if (timeLeft > 0 || resending) return;
    setError("");
    setResending(true);
    try {
      await sendVerificationCode(email);
      setTimeLeft(60);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to resend code.");
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const enteredCode = code.join("");
    if (enteredCode.length < 6) {
      alert("Please enter all 6 digits.");
      return;
    }

    setLoading(true);
    try {
      setError("");
      const result = await verifyEmailCode(email, enteredCode);
      // ensure token persisted even if service didn't handle
      if (result?.verification_token) {
        try { localStorage.setItem("verificationToken", result.verification_token); } catch (_) {}
      }
      navigate("/register-details", { state: { email } });
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Error verifying code. Please try again later.");
    }
    setLoading(false);
  };

  return (
    <Layout>
      <Link
        to="/signup"
        className="text-sm text-gray-500 hover:underline flex items-center mb-6"
      >
       {/* Back button */}
       <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" transform="scale(-1,1) translate(-20,0)" />
            </svg>
            Back
          </button>
      </Link>

      <div className="max-w-sm mx-auto">
        <h2 className="text-2xl font-semibold mb-2">Verify your identity</h2>
        <p className="text-sm text-gray-500 mb-6">
          You must enter the 6-digit code we sent to{" "}
          <span className="font-medium">{email}</span> in the field below.
        </p>

        <form onSubmit={handleVerify}>
          <div className="bg-gray-100 p-6 rounded-lg text-center mb-6">
            <div className="flex justify-center gap-2 mb-4">
              {code.map((digit, i) => (
                <input
                  key={i}
                  id={`code-${i}`}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, i)}
                  className="w-12 h-12 border border-gray-300 rounded text-center text-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              ))}
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={handleResend}
                disabled={timeLeft > 0 || resending}
                className={`px-4 py-2 text-sm font-medium transition ${
                  timeLeft > 0
                    ? "text-gray-500 cursor-not-allowed"
                    : "hover:bg-gray-700"
                }`}
              >
                {resending ? "Sending..." : "Request new code"}
                {timeLeft > 0 && !resending && ` (in ${timeLeft}s)`}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1E1E2F] text-white rounded-md py-2 text-sm font-medium hover:bg-gray-800 mb-4"
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
        </form>
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

        <p className="text-center text-sm text-gray-500">
          Are you having problems?{" "}
          <Link
            to="/contact"
            className="text-[#6B7280]-600 font-medium hover:underline"
          >
            Contact us
          </Link>
        </p>
      </div>
    </Layout>
  );
}
